/**
 * Text somebody else wrote, drawn safely, with just enough formatting.
 *
 * Everything a storyboard carries - line text, the deck's prose in `meta`,
 * prompts, the strategist's notes, comments - is text an agent or a person
 * typed, and some of it carries markup (the deck's handover lines wrap their
 * titles in `<b>`). None of it is trusted. So nothing here ever reaches the
 * DOM as HTML: no `dangerouslySetInnerHTML`, no markdown renderer that passes
 * tags through. The text is split into React elements and React escapes every
 * character of it.
 *
 * What a note needs and nothing more:
 *  - **paragraphs** - a blank line starts a new one; a single line break stays
 *    a line break, because notes are typed in a textarea, not composed as prose;
 *  - **bullets** - a line starting `- `, `* ` or `• ` is a list item, and
 *    consecutive ones are one list ("one or two sentences, can be with bullet
 *    points", in the owner's words);
 *  - **bold** - `**like this**`, and the deck's own `<b>like this</b>` (and
 *    `<strong>`), which are read as the same mark rather than shown as tags.
 *
 * No links (a URL stays plain text: nothing here should send somebody away
 * from a brief on one click of text an agent wrote), no headings, no italics,
 * no other tag. Anything else shows exactly as it was typed.
 */
import { Fragment, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface SafeTextProps {
  text: string | null | undefined;
  /**
   * One run of text inside a sentence (a title, a table cell): bold only, line
   * breaks kept, no paragraphs or lists, rendered in a `<span>`.
   */
  inline?: boolean;
  className?: string;
}

const BULLET = /^\s*(?:[-*•])\s+/;

/** `**bold**`, `<b>bold</b>`, `<strong>bold</strong>` - the one mark. Case-insensitive tags, no attributes. */
const BOLD = /\*\*([^*\n](?:[^*]*?[^*\n])?)\*\*|<(b|strong)>([\s\S]*?)<\/\2>/gi;

/** A run of text with its bold marks turned into `<strong>`, every other character escaped by React. */
function marks(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(BOLD)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <strong key={`${keyPrefix}-b${n++}`} className="font-semibold">
        {match[1] ?? match[3]}
      </strong>,
    );
    last = at + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Lines joined with `<br>`, each with its marks. */
function withBreaks(lines: string[], keyPrefix: string): ReactNode[] {
  return lines.map((line, index) => (
    <Fragment key={`${keyPrefix}-l${index}`}>
      {index > 0 && <br />}
      {marks(line, `${keyPrefix}-l${index}`)}
    </Fragment>
  ));
}

export type Block = { kind: "p"; lines: string[] } | { kind: "ul"; items: string[] };

/**
 * Blank lines split paragraphs; inside one, runs of bullet lines become lists.
 * Exported for the chat's message body, which lays a note out the same way.
 */
export function blocksOf(text: string): Block[] {
  const blocks: Block[] = [];
  for (const chunk of text.replace(/\r\n?/g, "\n").split(/\n[ \t]*\n/)) {
    let current: Block | null = null;
    for (const raw of chunk.split("\n")) {
      if (raw.trim() === "") continue;
      if (BULLET.test(raw)) {
        if (current?.kind !== "ul") blocks.push((current = { kind: "ul", items: [] }));
        current.items.push(raw.replace(BULLET, ""));
      } else {
        if (current?.kind !== "p") blocks.push((current = { kind: "p", lines: [] }));
        current.lines.push(raw.trimEnd());
      }
    }
  }
  return blocks;
}

export function SafeText({ text, inline = false, className }: SafeTextProps) {
  if (!text) return null;

  if (inline) {
    return (
      <span className={cn("wrap-anywhere", className)}>
        {withBreaks(text.replace(/\r\n?/g, "\n").split("\n"), "i")}
      </span>
    );
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-2 wrap-anywhere", className)}>
      {blocksOf(text).map((block, index) =>
        block.kind === "ul" ? (
          <ul key={index} className="flex list-disc flex-col gap-1 ps-5 marker:text-current/50">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{marks(item, `${index}-${itemIndex}`)}</li>
            ))}
          </ul>
        ) : (
          <p key={index}>{withBreaks(block.lines, String(index))}</p>
        ),
      )}
    </div>
  );
}
